import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import type { TransientBannerVariant } from "./useTransientMessage";

const BANNER_SPRING = { type: "spring" as const, stiffness: 520, damping: 36, mass: 0.7 };

type TransientStatusBannerProps = {
  message: string | null;
  variant?: TransientBannerVariant;
  onDismiss: () => void;
  dismissLabel: string;
  className?: string;
};

export function TransientStatusBanner({
  message,
  variant = "success",
  onDismiss,
  dismissLabel,
  className = "",
}: TransientStatusBannerProps) {
  const prefersReducedMotion = useReducedMotion();
  const isError = variant === "error";

  return (
    <AnimatePresence mode="wait">
      {message ? (
        <motion.div
          key={message}
          role={isError ? "alert" : "status"}
          aria-live={isError ? "assertive" : "polite"}
          className={`liquid-glass-pill flex h-auto min-h-9 w-full min-w-0 self-start items-start gap-2 rounded-xl px-3 py-1.5 text-sm ${
            isError ? "text-[oklch(88%_0.025_35)]" : "text-[oklch(84%_0.03_155)]"
          } ${className}`}
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 28 }}
          transition={prefersReducedMotion ? { duration: 0.15 } : BANNER_SPRING}
        >
          <span className="min-w-0 flex-1 break-words">{message}</span>
          <button
            type="button"
            className="-my-0.5 shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-[oklch(72%_0.09_155)] transition-colors active:bg-[oklch(24%_0.025_165)]"
            onClick={onDismiss}
          >
            {dismissLabel}
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
